import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setCollectionsList } from "../../reducer/collection/Collection.action";
import { showToaster } from "../../common/common";
import PostRequest from "../../services/PostRequest";
import { useNavigate } from "react-router-dom";
import { McaDrawer } from "../../components/mca";

import {
  apiOptions,
  daysOfMonth,
  daysOfWeek,
  fetchModeOptions,
  hoursOptions,
  minutesFrequencyOptions,
  scrapeTypeOptions,
  REFINEMENT_DOMAINS,
} from "../../data/constants";
import { ChooseAPI } from "../../common/custom-options";
import { CategoryFields, dollarsToCents } from "./requests/CategoryFields";

const getOptionFromValue = (value) => {
  return apiOptions.find((op) => op.value == value);
};

// Surface a category create error. PostRequest throws Error(message=JSON string
// of the failure body), so parse it and special-case the two first-class UX
// errors (no-cookies → Postcodes, no-credits → Account) with a CTA redirect.
const surfaceCategoryError = (error, navigate) => {
  let errs = null;
  try {
    errs = JSON.parse(error.message);
  } catch (e) {
    /* plain string message */
  }
  if (errs && typeof errs === "object") {
    if (errs.postcode && /no cookies|generate cookies/i.test(String(errs.postcode))) {
      showToaster(`${errs.postcode} Opening the Postcodes page…`, "error");
      navigate("/postcodes");
      return;
    }
    if (errs.credits) {
      showToaster(String(errs.credits), "error");
      navigate("/account");
      return;
    }
    const k = Object.keys(errs)[0];
    const v = errs[k];
    showToaster(`${k}: ${Array.isArray(v) ? v.join(", ") : v}`, "error");
    return;
  }
  showToaster(error.message, "error");
};

const EMPTY_CATEGORY = {
  domainId: "",
  domain: "",
  postcode: "",
  category_url: "",
  amazon_node_id: "",
  nodeTouched: false,
  price_min: "",
  price_max: "",
  filter_prime_ships_from_au: false,
  filter_delivery_day: "",
};

const CollectionForm = ({
  show,
  handleClose,
  setShowRequests,
  setCollectionId,
  activeCollection = {},
  activeModal,
  setActiveModal,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [collectionName, setCollectionName] = useState("");
  const [notifEmail, setNotifEmail] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState("Daily");
  const [selectedOption, setSelectedOption] = useState({});
  const [selectedPriority, setSelectedPriority] = useState("Normal");
  const [status, setStatus] = useState(true);
  const [selectedFetchMode, setSelectedFetchMode] = useState(
    fetchModeOptions[0]
  );
  const [scrapeType, setScrapeType] = useState("product");
  const [categoryData, setCategoryData] = useState(EMPTY_CATEGORY);
  const patchCategory = (p) => setCategoryData((c) => ({ ...c, ...p }));

  // Multiselect state for days_of_week, days_of_month, hours
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState(new Set());
  const [selectedDaysOfMonth, setSelectedDaysOfMonth] = useState(new Set());
  const [selectedHours, setSelectedHours] = useState(new Set());
  const [selectedMinutesFreq, setSelectedMinutesFreq] = useState(null);

  const handleCreateCollection = async (e) => {
    try {
      e.preventDefault();

      // Category scrape: one-call create-and-dispatch, then jump to the records view.
      if (selectedOption?.value === "amazon" && scrapeType === "category") {
        const cd = categoryData;
        if (!cd.domain) return showToaster("Select an Amazon domain.", "error");
        if (!/^https?:\/\//i.test(cd.category_url || ""))
          return showToaster("Enter a valid category URL.", "error");
        if (!cd.amazon_node_id)
          return showToaster("Amazon node ID is required.", "error");
        if (!cd.postcode)
          return showToaster("Select a postcode (cookies required).", "error");

        const minC = cd.price_min === "" ? 0 : dollarsToCents(cd.price_min);
        const maxC = cd.price_max === "" ? 10000000 : dollarsToCents(cd.price_max);
        if (maxC < minC)
          return showToaster("Max price must be ≥ min price.", "error");

        const supportsRef = REFINEMENT_DOMAINS.includes(cd.domain);
        const body = {
          name: collectionName || undefined,
          category_url: cd.category_url,
          amazon_node_id: cd.amazon_node_id,
          domain: cd.domain, // STRING key, not id
          postcode: cd.postcode, // STRING zipcode, not id
          price_min_cents: minC,
          price_max_cents: maxC,
          notification_email: notifEmail || undefined,
          priority: selectedPriority,
          ...(supportsRef
            ? {
                filter_prime_ships_from_au: !!cd.filter_prime_ships_from_au,
                ...(cd.filter_delivery_day
                  ? { filter_delivery_day: cd.filter_delivery_day }
                  : {}),
              }
            : {}),
        };
        try {
          const res = await PostRequest(
            "/schedules/amazon/category/add_schedule/",
            body,
            { showLoader: "add-new-collection" }
          );
          handleClose();
          dispatch(setCollectionsList());
          navigate(`/collections/${res.id}/records`); // res = {id, runner_id}
        } catch (error) {
          surfaceCategoryError(error, navigate);
        }
        return; // never fall through to the product create flow
      }

      const formData = new FormData(e.target);

      let scheduleTime = {};

      const baseSchedule = {
        hours: Array.from(selectedHours),
      };

      if (
        selectedSchedule === "Every X Minutes" ||
        selectedSchedule === "Manual"
      ) {
        scheduleTime = {
          minutes_frequency: selectedMinutesFreq,
          days_of_month: Array.from(selectedDaysOfMonth),
          days_of_week: Array.from(selectedDaysOfWeek),
          ...baseSchedule,
        };
      } else if (selectedSchedule === "Weekly") {
        scheduleTime = {
          days_of_week: Array.from(selectedDaysOfWeek),
          ...baseSchedule,
        };
      } else if (selectedSchedule === "Monthly") {
        scheduleTime = {
          days_of_month: Array.from(selectedDaysOfMonth),
          ...baseSchedule,
        };
      } else if (selectedSchedule === "Daily") {
        scheduleTime = baseSchedule;
      }

      const data = {
        name: formData.get("name"),
        request_type: selectedOption?.value || "amazon",
        schedule: selectedSchedule,
        schedule_time: scheduleTime,
        priority: selectedPriority,
        notification_email: formData.get("notification_email") || "",
        status: status == true ? "enable" : "disabled",
      };

      // Include fetch_mode for Officeworks collections
      if (["officeworks", "repco"].includes(selectedOption?.value) && selectedFetchMode) {
        data.fetch_mode = selectedFetchMode.value;
      }

      const option = {
        showLoader: "add-new-collection",
      };
      const response = await PostRequest(
        "/schedules/add_schedule/",
        data,
        option
      );
      setCollectionId(response);
      handleClose();
      setShowRequests(true);
    } catch (error) {
      console.log(error.message);
      showToaster(error.message, "error");
    }
  };

  const handleUpdateCollection = async (e) => {
    try {
      e.preventDefault();
      const formData = new FormData(e.target);

      let scheduleTime = {};

      const baseSchedule = {
        hours: Array.from(selectedHours),
      };

      if (
        selectedSchedule === "Every X Minutes" ||
        selectedSchedule === "Manual"
      ) {
        scheduleTime = {
          minutes_frequency: selectedMinutesFreq,
          days_of_month: Array.from(selectedDaysOfMonth),
          days_of_week: Array.from(selectedDaysOfWeek),
          ...baseSchedule,
        };
      } else if (selectedSchedule === "Weekly") {
        scheduleTime = {
          days_of_week: Array.from(selectedDaysOfWeek),
          ...baseSchedule,
        };
      } else if (selectedSchedule === "Monthly") {
        scheduleTime = {
          days_of_month: Array.from(selectedDaysOfMonth),
          ...baseSchedule,
        };
      } else if (selectedSchedule === "Daily") {
        scheduleTime = baseSchedule;
      }

      const data = {
        schedule_id: activeModal,
        name: formData.get("name"),
        request_type: selectedOption?.value || "amazon",
        schedule: selectedSchedule,
        schedule_time: scheduleTime,
        priority: selectedPriority,
        notification_email: formData.get("notification_email") || "",
        status: status == true ? "enable" : "disabled",
      };

      // Include fetch_mode for Officeworks collections
      if (["officeworks", "repco"].includes(selectedOption?.value) && selectedFetchMode) {
        data.fetch_mode = selectedFetchMode.value;
      }

      const option = {
        showLoader: "add-new-collection",
      };

      await PostRequest("/schedules/update_schedule/", data, option);
      showToaster("Collection updated successfully.");
      setActiveModal(false);
      dispatch(setCollectionsList());
    } catch (error) {
      console.log(error.message);
      showToaster(error.message, "error");
    }
  };

  const getSingleCollection = async () => {
    try {
      const response = await PostRequest("/schedules/get/", {
        schedule_id: activeModal,
      });
      // Prepopulate form from response
      if (response?.name != null) setCollectionName(response.name);
      if (response?.notification_email != null)
        setNotifEmail(response.notification_email);
      setSelectedOption(getOptionFromValue(response?.request_type));
      setSelectedSchedule(response?.schedule || "Daily");
      setSelectedPriority(response?.priority || "Normal");
      setStatus(response?.status == "disabled" ? false : true);

      // Restore fetch_mode for Officeworks collections
      if (response?.fetch_mode) {
        const mode = fetchModeOptions.find(
          (o) => o.value === response.fetch_mode
        );
        if (mode) setSelectedFetchMode(mode);
      }

      // Restore schedule_time selections
      if (response?.schedule_time) {
        setSelectedDaysOfWeek(
          new Set(response.schedule_time.days_of_week || [])
        );
        setSelectedDaysOfMonth(
          new Set(response.schedule_time.days_of_month || [])
        );
        setSelectedHours(new Set(response.schedule_time.hours || []));
        setSelectedMinutesFreq(response.schedule_time.minutes_frequency || null);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleToggle = (event) => {
    setStatus(event.target.checked);
  };

  const handleRequestTypeChange = (value) => {
    setSelectedOption(getOptionFromValue(value));
    // Reset fetch mode to full when switching API type
    setSelectedFetchMode(fetchModeOptions[0]);
    setScrapeType("product");
  };

  const handleCancel = () => {
    if (activeModal) {
      setActiveModal(false);
    } else {
      handleClose();
    }
    // Reset form
    setSelectedSchedule("Daily");
    setSelectedDaysOfWeek(new Set());
    setSelectedDaysOfMonth(new Set());
    setSelectedHours(new Set());
    setSelectedMinutesFreq(null);
    setSelectedPriority("Normal");
    setStatus(true);
    setScrapeType("product");
    setCategoryData(EMPTY_CATEGORY);
  };

  useEffect(() => {
    if (activeModal) {
      // Seed text fields instantly from the row we already have, then let the
      // /schedules/get/ response (above) overwrite with the authoritative values.
      setCollectionName(activeCollection?.name || "");
      setNotifEmail(activeCollection?.notification_email || "");
      getSingleCollection();
    } else if (show) {
      // Reset for new collection
      setCollectionName("");
      setNotifEmail("");
      setSelectedSchedule("Daily");
      setSelectedOption(getOptionFromValue("amazon"));
      setSelectedPriority("Normal");
      setStatus(true);
      setSelectedDaysOfWeek(new Set());
      setSelectedDaysOfMonth(new Set());
      setSelectedHours(new Set());
      setSelectedMinutesFreq(null);
      setScrapeType("product");
      setCategoryData(EMPTY_CATEGORY);
    }
  }, [activeModal, show]);

  // Multiselect toggle helpers
  const toggleDayOfWeek = (value) => {
    const newSet = new Set(selectedDaysOfWeek);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    setSelectedDaysOfWeek(newSet);
  };

  const toggleDayOfMonth = (value) => {
    const newSet = new Set(selectedDaysOfMonth);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    setSelectedDaysOfMonth(newSet);
  };

  const toggleHour = (value) => {
    const newSet = new Set(selectedHours);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    setSelectedHours(newSet);
  };

  const isEdit = !!activeModal;
  const isCategoryCreate =
    !isEdit && selectedOption?.value === "amazon" && scrapeType === "category";
  const drawerTitle = isEdit ? "Edit Collection" : "New Collection";
  const drawerSubtitle = isEdit
    ? "Update the schedule & settings"
    : isCategoryCreate
    ? "Create & dispatch an Amazon category scrape"
    : "Step 1 of 2 · Define the schedule & settings";

  return (
    <McaDrawer
      open={show || !!activeModal}
      onClose={handleCancel}
      title={drawerTitle}
      subtitle={drawerSubtitle}
      footer={
        <>
          <button
            type="button"
            className="mca-btn mca-btn--outline"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="collection-form"
            className="mca-btn mca-btn--primary"
          >
            {isEdit
              ? "Update"
              : isCategoryCreate
              ? "Start category scrape →"
              : "Next: choose records →"}
          </button>
        </>
      }
    >
      <form
        id="collection-form"
        onSubmit={isEdit ? handleUpdateCollection : handleCreateCollection}
        className="form-stack"
      >
        {/* Name + Status */}
        <div className="mca-field">
          <label htmlFor="cf-name" className="mca-label">
            Collection name <span className="req">*</span>
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            className="mca-input"
            placeholder="e.g. AU Bestsellers — Daily"
            value={collectionName}
            onChange={(e) => setCollectionName(e.target.value)}
            required
          />
        </div>

        <label className="mca-switch" style={{ justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: "var(--mca-font-heading)", fontWeight: 600, fontSize: "14px" }}>
              Status
            </div>
            <div className="mca-help">Enabled collections run on their schedule.</div>
          </div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
            <span className="mca-help" style={{ fontWeight: 600 }}>
              {status ? "Enabled" : "Disabled"}
            </span>
            <span style={{ display: "inline-flex" }}>
              <input
                type="checkbox"
                checked={status}
                onChange={handleToggle}
              />
              <span className="mca-switch__track"></span>
            </span>
          </span>
        </label>

        <hr className="mca-hr" />

        {/* API Type + Fetch Mode */}
        <div className="sec-title">API</div>
        <div className="row-2">
          <div className="mca-field">
            <label htmlFor="cf-type" className="mca-label">
              Request type <span className="req">*</span>
            </label>
            <select
              id="cf-type"
              name="request_type"
              className="mca-select"
              value={selectedOption?.value || "amazon"}
              onChange={(e) => handleRequestTypeChange(e.target.value)}
            >
              {apiOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {["officeworks", "repco"].includes(selectedOption?.value) && (
            <div className="mca-field">
              <label htmlFor="cf-fetch" className="mca-label">
                Fetch mode <span className="req">*</span>
                <span className="mca-tip">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="var(--mca-muted)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M9.5 9a2.5 2.5 0 0 1 4 1.5c0 1.5-2 2-2 3" />
                    <path d="M12 17h.01" />
                  </svg>
                  <span className="mca-tip__bubble">
                    Availability-only is cheaper – stock status without full product data.
                  </span>
                </span>
              </label>
              <select
                id="cf-fetch"
                name="fetch_mode"
                className="mca-select"
                value={selectedFetchMode?.value || "full"}
                onChange={(e) => {
                  const mode = fetchModeOptions.find((o) => o.value === e.target.value);
                  if (mode) setSelectedFetchMode(mode);
                }}
              >
                {fetchModeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!isEdit && selectedOption?.value === "amazon" && (
            <div className="mca-field">
              <label htmlFor="cf-scrape-type" className="mca-label">
                Scrape type <span className="req">*</span>
              </label>
              <select
                id="cf-scrape-type"
                className="mca-select"
                value={scrapeType}
                onChange={(e) => setScrapeType(e.target.value)}
              >
                {scrapeTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {!isEdit &&
        selectedOption?.value === "amazon" &&
        scrapeType === "category" ? (
          <>
            <hr className="mca-hr" />
            <div className="sec-title">Category</div>
            <CategoryFields value={categoryData} onChange={patchCategory} />
          </>
        ) : (
          <>
            <hr className="mca-hr" />

            {/* Schedule */}
            <div className="sec-title">Schedule</div>
        <div className="sched" role="radiogroup" aria-label="Schedule">
          <label className="sched-opt">
            <input
              type="radio"
              name="schedule"
              value="Manual"
              checked={selectedSchedule === "Manual"}
              onChange={(e) => setSelectedSchedule(e.target.value)}
            />
            <span className="sched-opt__lab">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 3v4M3 5h4" />
                <path d="m6 12 4 4 8-8" />
              </svg>
              Manual
            </span>
          </label>
          <label className="sched-opt">
            <input
              type="radio"
              name="schedule"
              value="Every X Minutes"
              checked={selectedSchedule === "Every X Minutes"}
              onChange={(e) => setSelectedSchedule(e.target.value)}
            />
            <span className="sched-opt__lab">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              Minutes
            </span>
          </label>
          <label className="sched-opt">
            <input
              type="radio"
              name="schedule"
              value="Daily"
              checked={selectedSchedule === "Daily"}
              onChange={(e) => setSelectedSchedule(e.target.value)}
            />
            <span className="sched-opt__lab">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="4.5" />
                <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
              </svg>
              Daily
            </span>
          </label>
          <label className="sched-opt">
            <input
              type="radio"
              name="schedule"
              value="Weekly"
              checked={selectedSchedule === "Weekly"}
              onChange={(e) => setSelectedSchedule(e.target.value)}
            />
            <span className="sched-opt__lab">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4.5" width="18" height="16" rx="2" />
                <path d="M3 9h18M8 2.5v4" />
              </svg>
              Weekly
            </span>
          </label>
          <label className="sched-opt">
            <input
              type="radio"
              name="schedule"
              value="Monthly"
              checked={selectedSchedule === "Monthly"}
              onChange={(e) => setSelectedSchedule(e.target.value)}
            />
            <span className="sched-opt__lab">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4.5" width="18" height="16" rx="2" />
                <path d="M3 9h18M16 2.5v4M8 2.5v4" />
              </svg>
              Monthly
            </span>
          </label>
        </div>

        {/* Schedule cascade container */}
        <div
          className={`cascade ${[
            "Every X Minutes",
            "Weekly",
            "Monthly",
            "Daily",
          ].includes(selectedSchedule) ? "show" : ""}`}
        >
          {/* Minutes frequency */}
          {selectedSchedule === "Every X Minutes" && (
            <div className="mca-field">
              <label htmlFor="minutes-freq" className="mca-label">
                Run every
              </label>
              <select
                id="minutes-freq"
                name="minutes_frequency"
                className="mca-select"
                value={selectedMinutesFreq || ""}
                onChange={(e) => setSelectedMinutesFreq(e.target.value)}
              >
                <option value="">Select frequency...</option>
                {minutesFrequencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    Every {opt.label.toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Days of week - for Weekly */}
          {selectedSchedule === "Weekly" && (
            <div className="mca-field">
              <label className="mca-label">
                Days of week{" "}
                <span className="mca-help" style={{ fontWeight: 400 }}>
                  (leave empty for all days)
                </span>
              </label>
              <McaMultiSelect
                options={daysOfWeek}
                selected={selectedDaysOfWeek}
                onToggle={toggleDayOfWeek}
                placeholder="All days"
                name="days_of_week"
              />
            </div>
          )}

          {/* Days of month - for Monthly or Every X Minutes */}
          {(selectedSchedule === "Monthly" ||
            selectedSchedule === "Every X Minutes") && (
            <div className="mca-field">
              <label className="mca-label">
                Days of month{" "}
                <span className="mca-help" style={{ fontWeight: 400 }}>
                  (leave empty for all days)
                </span>
              </label>
              <McaMultiSelect
                options={daysOfMonth}
                selected={selectedDaysOfMonth}
                onToggle={toggleDayOfMonth}
                placeholder="All days"
                name="days_of_month"
              />
            </div>
          )}

          {/* Days of week - for Every X Minutes */}
          {selectedSchedule === "Every X Minutes" && (
            <div className="mca-field">
              <label className="mca-label">
                Days of week{" "}
                <span className="mca-help" style={{ fontWeight: 400 }}>
                  (leave empty for all days)
                </span>
              </label>
              <McaMultiSelect
                options={daysOfWeek}
                selected={selectedDaysOfWeek}
                onToggle={toggleDayOfWeek}
                placeholder="All days"
                name="days_of_week"
              />
            </div>
          )}

          {/* Hours - for Daily, Weekly, Monthly, Every X Minutes */}
          {["Daily", "Weekly", "Monthly", "Every X Minutes"].includes(
            selectedSchedule
          ) && (
            <div className="mca-field">
              <label className="mca-label">
                Hours (UTC+10){" "}
                <span className="mca-help" style={{ fontWeight: 400 }}>
                  (leave empty for all hours)
                </span>
              </label>
              <McaMultiSelect
                options={hoursOptions}
                selected={selectedHours}
                onToggle={toggleHour}
                placeholder="All hours"
                name="hours"
              />
            </div>
          )}
        </div>
          </>
        )}

        <hr className="mca-hr" />

        {/* Priority */}
        <div className="sec-title">Priority</div>
        <div className="prio" role="radiogroup" aria-label="Priority">
          <label className="prio-opt">
            <input
              type="radio"
              name="priority"
              value="Highest"
              checked={selectedPriority === "Highest"}
              onChange={(e) => setSelectedPriority(e.target.value)}
            />
            <span className="prio-opt__lab">
              <span
                className="pdot"
                style={{ background: "var(--mca-danger)" }}
              ></span>
              Highest
            </span>
          </label>
          <label className="prio-opt">
            <input
              type="radio"
              name="priority"
              value="High"
              checked={selectedPriority === "High"}
              onChange={(e) => setSelectedPriority(e.target.value)}
            />
            <span className="prio-opt__lab">
              <span
                className="pdot"
                style={{ background: "var(--mca-warning)" }}
              ></span>
              High
            </span>
          </label>
          <label className="prio-opt">
            <input
              type="radio"
              name="priority"
              value="Normal"
              checked={selectedPriority === "Normal"}
              onChange={(e) => setSelectedPriority(e.target.value)}
            />
            <span className="prio-opt__lab">
              <span
                className="pdot"
                style={{ background: "var(--mca-success)" }}
              ></span>
              Normal
            </span>
          </label>
          <label className="prio-opt">
            <input
              type="radio"
              name="priority"
              value="Low"
              checked={selectedPriority === "Low"}
              onChange={(e) => setSelectedPriority(e.target.value)}
            />
            <span className="prio-opt__lab">
              <span
                className="pdot"
                style={{ background: "var(--mca-info)" }}
              ></span>
              Low
            </span>
          </label>
          <label className="prio-opt">
            <input
              type="radio"
              name="priority"
              value="Lowest"
              checked={selectedPriority === "Lowest"}
              onChange={(e) => setSelectedPriority(e.target.value)}
            />
            <span className="prio-opt__lab">
              <span
                className="pdot"
                style={{ background: "var(--mca-muted)" }}
              ></span>
              Lowest
            </span>
          </label>
        </div>

        <hr className="mca-hr" />

        {/* Notifications */}
        <div className="sec-title">Notifications</div>
        <div className="mca-field">
          <label htmlFor="cf-email" className="mca-label">
            Notification email{" "}
            <span className="mca-help" style={{ fontWeight: 400 }}>
              (optional)
            </span>
          </label>
          <div className="mca-input-group">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
            <input
              id="cf-email"
              name="notification_email"
              type="email"
              className="mca-input"
              placeholder="alerts@example.com"
              value={notifEmail}
              onChange={(e) => setNotifEmail(e.target.value)}
            />
          </div>
          <span className="mca-help">
            We'll email this address when a run completes or fails.
          </span>
        </div>

        <div className="mca-alert mca-alert--info">
          <svg
            className="lead"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="m8.5 12 2.5 2.5 4.5-5" />
          </svg>
          <div className="mca-alert__body">
            <strong>Destinations are coming soon.</strong> You'll be able to
            auto-deliver results to S3, FTP, or a webhook.
          </div>
        </div>
      </form>
    </McaDrawer>
  );
};

/**
 * McaMultiSelect – Chip-based multiselect control.
 * Inline component for rendering the mca-ms pattern from design.
 */
function McaMultiSelect({
  options,
  selected,
  onToggle,
  placeholder = "Select…",
  name,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleChipRemove = (value) => {
    onToggle(value);
  };

  const handleOptionClick = (value) => {
    onToggle(value);
  };

  return (
    <div
      className={`mca-ms ${isOpen ? "is-open" : ""}`}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div className="mca-ms__control">
        {selected.size === 0 ? (
          <span className="mca-ms__ph">{placeholder}</span>
        ) : (
          options.map((opt) => {
            if (!selected.has(opt.value)) return null;
            return (
              <span key={opt.value} className="mca-ms__chip">
                {opt.label}
                <button
                  type="button"
                  aria-label={`Remove ${opt.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChipRemove(opt.value);
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })
        )}
        <svg
          className="mca-ms__chev"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
      {isOpen && (
        <div className="mca-ms__menu">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="mca-ms__opt"
              onClick={() => handleOptionClick(opt.value)}
            >
              <span className="mca-check" style={{ pointerEvents: "none" }}>
                <input
                  type="checkbox"
                  value={opt.value}
                  checked={selected.has(opt.value)}
                  readOnly
                />
                <span className="mca-check__box">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
              </span>
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default CollectionForm;
